import type { CollabUser } from "./CollabSessionContext";
import { colorForUser } from "./userColor";

export const TRUSTED_BINDINGS_MESSAGE_TYPE = "presence.bindings.v1";
const MAX_BINDING_LIFETIME_MS = 120_000;

export interface TrustedPresenceBinding {
    user: CollabUser;
    expiresAt: number;
}

/**
 * Server stateless message contract:
 * {type:"presence.bindings.v1",version:1,bindings:[
 *   {clientId:<Y awareness client id>,user:{userid,name},expiresAt:<epoch ms>}
 * ]}
 *
 * Every valid message is a full room snapshot and replaces prior bindings.
 */
export function parseTrustedBindingSnapshot(
    payload: string,
    now = Date.now()
): Map<number, TrustedPresenceBinding> | null {
    let value: unknown;
    try {
        value = JSON.parse(payload);
    } catch {
        return null;
    }
    if (!value || typeof value !== "object") return null;
    const data = value as Record<string, unknown>;
    if (data.type !== TRUSTED_BINDINGS_MESSAGE_TYPE || data.version !== 1 || !Array.isArray(data.bindings)) return null;
    const result = new Map<number, TrustedPresenceBinding>();
    for (const raw of data.bindings) {
        if (!raw || typeof raw !== "object") return null;
        const binding = raw as Record<string, unknown>;
        const user = binding.user as Record<string, unknown> | undefined;
        if (
            typeof binding.clientId !== "number" || !Number.isSafeInteger(binding.clientId) || binding.clientId < 0 ||
            result.has(binding.clientId) ||
            !user || typeof user.userid !== "number" || !Number.isSafeInteger(user.userid) || user.userid <= 0 ||
            typeof user.name !== "string" || !user.name.trim() ||
            typeof binding.expiresAt !== "number" || !Number.isFinite(binding.expiresAt) ||
            binding.expiresAt <= now || binding.expiresAt - now > MAX_BINDING_LIFETIME_MS
        ) return null;
        result.set(binding.clientId, {
            user: { userid: user.userid, name: user.name.trim(), color: colorForUser(user.userid) },
            expiresAt: binding.expiresAt
        });
    }
    return result;
}
